import _ from "lodash";
// utils
import {
  filterFieldsBasedOnConfig,
  getIsUnitPlanUpdateModalEnabled,
  getIsCustomWeights,
  getIsEnabledLTITools,
  getIsScoreBasedRubricEnabled,
  getIsNewGradingSetupEnabled,
  getIsAssessmentGradeScaleToolEnabled,
  getIsEnableLearningGoalsEvaluationInNewGradingSetup,
} from "ClassRoom/modules/utils";
import {
  getPYPElementSetFromNodes,
  generateRandomIdV2,
  AESDecrypt,
  getMemoizedFunction,
  getMemoizedLangFunction,
  orderNodes,
  checkIsNodeTopParent,
} from "Utils";
import ACLStore from "lib/aclStore";

import update from "immutability-helper";

// modules
import {
  getAssessmentCourseDetailsFromCache,
  getAssessmentLearningCourseDetailsFromCache,
} from "Assessments/modules/AssessmentGraphqlHelpers";
import {
  getLocalisedTemplate,
  updateTemplate,
} from "IBPlanner/modules/IBPlannerModule";

// constants
import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_PYP,
  ELEMENT_TYPE_MYP_ATL,
  ELEMENT_TYPE_DP_ATL,
  ELEMENT_TYPE_UBD_ATL,
  ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  ELEMENT_TYPE_DP_LEARNING_STANDARD,
  ELEMENT_TYPE_MYP_OBJECTIVES,
  ELEMENT_TYPE_MYP_RELATED_CONCEPT,
  ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT,
  ELEMENT_TYPE_DP_SYLLABUS,
  ELEMENT_TYPE_MYP_LEARNER_PROFILE,
  ELEMENT_TYPE_DP_LEARNER_PROFILE,
  ELEMENT_TYPE_MYP_KEY_CONCEPT,
  TEMPLATE_FIELD_INQUIRY_QUESTIONS,
  TEMPLATE_FIELD_LINES_OF_INQUIRY,
  ELEMENT_TYPE_PYP_ATL,
  ELEMENT_TYPE_PYP_LEARNER_PROFILE,
  ELEMENT_TYPE_PYP_KEY_CONCEPT,
  PLANNER_ELEMENT_NESTED_CHECKLIST,
  TEMPLATE_FIELD_INQUIRY_QUESTIONS_V2,
  TEMPLATE_FIELD_UN_GOALS,
  TEMPLATE_FIELD_UN_GOALS_ASSESSMENT,
  ASSIGNMENT_TYPE_QUICK_TASK,
  SUBJECT_TYPE_TOK,
  SUBJECT_TYPE_OTHERS,
  CURRICULUM_TYPE_MYP,
  ELEMENT_TYPE_PYP_LEARNING_STANDARD,
  SUBJECT_TYPE_GENERIC,
  UNIT_TYPE_DP,
  MYP_INTERDISCIPLINARY,
  CURRICULUM_TYPE_UBD,
  ASSESSMENT,
  UNIT,
  TASK_TYPE_AI_TUTOR,
  TASK_TYPE_QUICK_TASK,
  ASSESSMENT_TYPE_AI_TUTOR,
  SUBJECT_NAME_ENVIRONMENTAL_SYSTEMS_SOCIETIES,
  DP_EXPERIMENTAL_PROGRAMME,
  GRADE_SCALE_TOOL,
} from "Constants/stringConstants";
import {
  ASSESSMENT_TYPE_WORKSHEET,
  TASK_TYPE_WORKSHEET,
} from "Worksheet/modules/WorksheetConstants";
import { resourceLibrarySourceOrg } from "config";
import { setEncryptedJsonItem } from "services/localStorage";
import {
  getDataOptionsForCheckListWithPopup,
  getPopupDataForCheckListWithPopupMemoized,
} from "UnitPlans/modules/Utils";
import { EVALUATE_PLANNER_ELEMENTS_BY_CURRICULUM } from "modules/Services";
import { INTERDISCIPLINARY_FIELD_MAPPING } from "AppComponents/AIContextBar/modules/Constants";
import {
  getIsCourseBasedGradingEnabled,
  getIsSubjectBasedGradingEnabled,
  getIsLearningCoursesEnabled,
} from "LearningCourses/modules/Utils";
import { getIsConfigurationEnabledForCPAY } from "modules/PermissionModule";
import {
  USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
  ASSESSMENT_COMMON_PREFERENCE,
} from "Constants/permissionConstants";
import {
  getIsAITutorEnhancementsV2Enabled,
  getIsAITutorModalitiesEnabled,
  getIsAssessmentTypeConfigurationEnabled,
  getIsAssignmentsUnitTaggingIn2_0Enabled,
} from "Utils/ACLStoreUtils";

import { getIsSpecifiedConceptsTaggingEnabled } from "AppComponents/Post/utils";
import { getSettingValue } from "Utils/ConfigurationUtils";
import { getNodeParents, getAllNodeParents } from "Utils/nodeParentHelpers";
import { ASSESSMENT_COMMON_PREFERENCE_KEYS } from "./Constants";

import { getDatasetsOrderByArgs } from "Datasets/modules/Utils";

/**
 * Retrieves assessment common preference value from user/organization settings.
 */
export const getAssessmentCommonPreferenceValue = key => {
  if (!key) return null;

  const preferences = getSettingValue({
    name: ASSESSMENT_COMMON_PREFERENCE,
    readFromUserAndOrgLevel: true,
  });

  switch (key) {
    case ASSESSMENT_COMMON_PREFERENCE_KEYS.DATA_SET_SCOPE:
      return _.get(preferences, key, "ALL");
    default:
      return null;
  }
};

/**
 * Don't import below object from modules/Services because of cyclic dependency issue
 */

export const pypElementSetMapping = {
  lp: "learnerProfiles",
  action: "actions",
  concepts: "concepts",
  theme: "themes",
  atls: "atls",
  benchmarks: "benchmarks",
  subjects: "subjects",
  relatedConcepts: "relatedConcepts",
  subjectGroup: "subjectGroups",
  learningStandardUBD: "learningStandardUBD",
  atlsUBD: "atlsUBD",
  atlsMYP: "atlsMYP",
  learningStandardMYP: "learningStandardMYP",
};

export const GENERIC_TEMPLATES = [
  "pyp_quick_task",
  "generic_le",
  "myp_quick_task",
  "myp_le",
  "myp_fmt_assessment",
  "myp_smt_assessment",
  "ubd_quick_task",
  "ubd_le_toddle_2.0",
  "ubd_assessment_toddle_2.0",
  "dp_quick_task",
  "dp_le",
  "dp_fmt_assessment",
  "dp_smt_assessment",
  "pyp_ai_tutor",
  "myp_ai_tutor",
  "dp_ai_tutor",
  "ubd_ai_tutor",
  "pyp_worksheet",
  "myp_worksheet",
  "dp_worksheet",
  "ubd_worksheet",
];

export const getWorksheetSteps = t => [
  {
    label: t("common:details"),
    value: "WORKSHEET_QUESTION_CREATION",
    heading: t("classRoom:share_resources"),
    subHeading: t("classRoom:placeholder_for_subtext"),
  },
  {
    label: t("worksheet:questions"),
    value: "QUESTION_CREATION",
    heading: t("worksheet:questions"),
    subHeading: t("classRoom:classes_discussion_shared_with"),
  },
  {
    label: t("classRoom:assign_settings"),
    value: "SETTINGS",
    heading: t("classRoom:assign_settings"),
    subHeading: t("classRoom:classes_discussion_shared_with"),
  },
];

/**
 * @returns updated stepList by removing specified fields from each step's fields array.
 */
export const updateStepList = ({ step_list, fieldsToRemove }) => {
  return _.forEach(step_list, (step, key) => {
    step_list[key] = {
      ...step,
      fields: _.filter(
        _.get(step, "fields", []),
        field => !_.includes(fieldsToRemove, field)
      ),
    };
  });
};

export const STANDARD_UIDS = {
  [CURRICULUM_TYPE_PYP]: "benchmarks",
  [CURRICULUM_TYPE_UBD]: "learningStandardUBD",
  [CURRICULUM_TYPE_MYP]: "learningStandardMYP",
  [CURRICULUM_TYPE_DP]: "subjectStandardDP",
};

export const FIELD_KEY_TO_PLANNER_ELEMENT_TYPE = {
  benchmarks: ELEMENT_TYPE_PYP_LEARNING_STANDARD,
  learningStandardUBD: ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  learningStandardMYP: ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  subjectStandardDP: ELEMENT_TYPE_DP_LEARNING_STANDARD,
};

export const allowLearningGoalsRatingInStandards = ({
  assessmentFields,
  curriculumProgramType,
  assessmentTools,
}) => {
  const _assessmentTools = !_.isEmpty(assessmentTools?.edges)
    ? assessmentTools?.edges
    : assessmentTools;
  const assessmentToolNodes = _.map(_assessmentTools, "node");

  const isStandardRubricPresent = !_.isEmpty(
    _.find(assessmentToolNodes, {
      assessmentToolType: "STANDARD_BASED_RUBRIC",
    }) || {}
  );
  // If standard rubric is present, we don't allow learning goals rating in standards
  if (isStandardRubricPresent) {
    return false;
  }

  const standardUID = STANDARD_UIDS[curriculumProgramType];

  const fieldValue = getFieldValue({
    fields: assessmentFields,
    uid: standardUID,
  });
  const nodes = getResolvedMinimalTreeSet({
    uid: standardUID,
    fields: assessmentFields,
    curriculumType: curriculumProgramType,
  });

  // Allow learning goals rating if ANY tagged standard is individually graded
  return (
    !_.isEmpty(fieldValue) &&
    _.some(fieldValue, id => {
      const node = _.find(nodes, { id }) || {};
      return !_.isEmpty(node) && node?.gradingType === "INDIVIDUAL";
    })
  );
};

export const allowLearningGoalsRatingInSkills = ({
  fieldUID,
  assessmentFields,
  curriculumProgramType,
}) => {
  const fieldValue = getFieldValue({
    fields: assessmentFields,
    uid: fieldUID,
  });

  const nodes = getResolvedMinimalTreeSet({
    uid: fieldUID,
    fields: assessmentFields,
    curriculumType: curriculumProgramType,
  });

  return (
    !_.isEmpty(fieldValue) &&
    _.some(fieldValue, id => {
      const node = _.find(nodes, { id }) || {};
      if (_.isEmpty(node)) return false;
      const properties =
        node.plannerElementNodeWrapper?.properties ??
        node.dataSetNodeWrapper?.properties;
      return properties?.isGradedStandard || properties?.isGraded;
    })
  );
};

const getPlannerElementsToEvaluate = ({
  assessmentFields,
  curriculumProgramType,
  assessmentTools,
}) => {
  return _.filter(
    EVALUATE_PLANNER_ELEMENTS_BY_CURRICULUM[curriculumProgramType],
    ({ value }) => {
      const fieldValue = getFieldValue({
        fields: assessmentFields,
        uid: value,
      });
      if (
        value === STANDARD_UIDS[curriculumProgramType] &&
        getIsConfigurationEnabledForCPAY({
          configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
        })
      ) {
        return allowLearningGoalsRatingInStandards({
          assessmentFields,
          curriculumProgramType,
          assessmentTools,
        });
      }
      if (
        getIsConfigurationEnabledForCPAY({
          configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
        }) &&
        getIsEnableLearningGoalsEvaluationInNewGradingSetup()
      ) {
        return allowLearningGoalsRatingInSkills({
          fieldUID: value,
          assessmentFields,
          curriculumProgramType,
        });
      }
      return !_.isEmpty(fieldValue);
    }
  );
};

export const checkAssessmentHasPypEvaluation = ({
  assessmentFields,
  curriculumProgramType,
  assessmentTools,
}) => {
  const ratableElements = getPlannerElementsToEvaluate({
    assessmentFields,
    curriculumProgramType,
    assessmentTools,
  });

  // Enable if at least one tagged learning goal (across any type) is ratable
  return !_.isEmpty(ratableElements);
};

export const checkHasTaggedLearningGoals = ({
  assessmentFields,
  curriculumProgramType,
}) => {
  const plannerElements =
    EVALUATE_PLANNER_ELEMENTS_BY_CURRICULUM[curriculumProgramType] || [];
  return _.some(
    plannerElements,
    ({ value: uid }) =>
      !_.isEmpty(getFieldValue({ fields: assessmentFields, uid }))
  );
};

export const getLearningGoalsToFilter = params => {
  const { subjects, unitType } = params || {};

  const subjectType = _.get(subjects, "0.type");
  const isCPCoreSubject = _.includes(["LD", "PPS"], subjectType);

  const fieldsToFilter = ["enableRating"];

  if (isCPCoreSubject) {
    fieldsToFilter.push("subjectStandardDP");
  }

  if (_.includes([SUBJECT_TYPE_TOK, SUBJECT_TYPE_OTHERS], subjectType)) {
    fieldsToFilter.push("syllabusContentDP");
  }

  if (_.isEmpty(unitType)) {
    fieldsToFilter.push("inquiryQuestions", "inquiryQuestionsV2", "lois");
  } else if (
    !_.isEmpty(unitType) &&
    !_.includes(["ibInquiry", "ibMYPInterdisciplinary", "ubdInquiry"], unitType)
  ) {
    fieldsToFilter.push("interdisciplinaryCriteriaMYP");
  }

  return fieldsToFilter;
};

export const getPlannerElementNodesFromFieldOptions = ({
  fieldObjConfigOptions,
  resourceFieldValue,
  mode,
}) => {
  let options = fieldObjConfigOptions;
  if (mode === "view") {
    options = _.filter(fieldObjConfigOptions, ({ value }) =>
      _.includes(resourceFieldValue, value)
    );
  }

  return _.map(options, ({ value, label, subText }) => {
    return {
      id: value,
      label,
      subText,
      parent: null,
      levelId: "L1",
      children: [],
    };
  });
};

// temporary object which will be removed once we migrate learning goals to actual templates
const fieldPlannerElementTypeMappingForPyp = {
  atls: ELEMENT_TYPE_PYP_ATL,
  benchmarks: ELEMENT_TYPE_PYP_LEARNING_STANDARD,
  concepts: ELEMENT_TYPE_PYP_KEY_CONCEPT,
  lp: ELEMENT_TYPE_PYP_LEARNER_PROFILE,
};

const getNewMeasureAssessingOptions = ({
  t,
  options,
  curriculumType,
  assessmentType,
  isCPCoreSubject,
  taskTypeGroup,
}) => {
  // remove NONE option
  let updatedOptions = _.filter(options, ({ value }) => value !== "");

  // remove DP assessment criteria rubric for CP core subjects
  if (isCPCoreSubject) {
    updatedOptions = _.filter(
      updatedOptions,
      ({ key }) => !_.includes(["DP_ASSESSMENT_RUBRIC"], key)
    );
  }

  const isQuickTask =
    taskTypeGroup === TASK_TYPE_QUICK_TASK ||
    assessmentType === ASSIGNMENT_TYPE_QUICK_TASK;
  const isWorksheet =
    taskTypeGroup === TASK_TYPE_WORKSHEET ||
    assessmentType === ASSESSMENT_TYPE_WORKSHEET;

  const shouldAddScore = !isQuickTask && !isWorksheet;

  if (curriculumType !== CURRICULUM_TYPE_DP) {
    if (shouldAddScore) {
      if (curriculumType === "UBD") {
        updatedOptions?.unshift({
          label: t("score"),
          value: "SCORE",
          key: "SCORE",
        });
      } else {
        updatedOptions?.push({
          label: t("score"),
          value: "SCORE",
          key: "SCORE",
        });
      }
    }
  }

  // labels will get localized automatically through getOptionsLocale function (ResourceEdit/modules/Utils.js)
  if (getIsScoreBasedRubricEnabled() && shouldAddScore) {
    updatedOptions.splice(1, 0, {
      isSubMenu: true,
      key: "SCORE_BASED_RUBRIC",
      label: "Score based rubric",
      options: [
        {
          icon: "AddOutlined",
          key: "create_new",
          label: "Create new",
        },
        {
          icon: "AssessmentAddOutlined",
          key: "use_from_template",
          label: "Select a template",
        },
      ],
      value: "SCORE_BASED_RUBRIC",
    });
  }

  // labels will get localized automatically through getOptionsLocale function (ResourceEdit/modules/Utils.js)

  if (getIsNewGradingSetupEnabled() && shouldAddScore) {
    updatedOptions.splice(1, 0, {
      isSubMenu: true,
      key: "STANDARD_BASED_RUBRIC",
      label: "Standard based rubric",
      options: [
        {
          icon: "AddOutlined",
          key: "create_new",
          label: "Create new",
        },
        {
          icon: "AssessmentAddOutlined",
          key: "use_from_template",
          label: "Select a template",
        },
      ],
      value: "STANDARD_BASED_RUBRIC",
    });
  }

  if (getIsAssessmentGradeScaleToolEnabled() && shouldAddScore) {
    updatedOptions.splice(1, 0, {
      key: GRADE_SCALE_TOOL,
      value: GRADE_SCALE_TOOL,
      label: "Graded scale",
    });
  }

  return updatedOptions;
};

export const updateAssessmentTemplate = params => {
  const {
    t,
    mode,
    template,
    assessmentType,
    taskTypeGroup,
    curriculumType,
    isFromClassroom,
    resourceFields,
    subjects = [],
    userType,
    assessmentTools = [],
    learningCourseId,
    coursePrimaryGradeId,
    assessmentSubjectIds,
    isCourseBasedGrading,
    isUsingNewGradingSetupInCourses,
    sourceFrom,
  } = params;

  if (_.isEmpty(template)) return;

  const dynamicTemplate = _.cloneDeep(template);

  const subjectType = _.get(subjects, "0.type");
  const isCPCoreSubject = _.includes(["LD", "PPS"], subjectType);

  const measureAssessingField =
    dynamicTemplate?.field_list?.["measureAssessing"];
  if (measureAssessingField && measureAssessingField?.config?.options) {
    measureAssessingField.config.options = getNewMeasureAssessingOptions({
      t,
      options: measureAssessingField.config.options,
      assessmentType,
      taskTypeGroup,
      curriculumType,
      isCPCoreSubject,
      learningCourseId,
      coursePrimaryGradeId,
      assessmentSubjectIds,
      isCourseBasedGrading,
      isUsingNewGradingSetupInCourses,
    });
  }
  const isRoep = _.isEqual(assessmentType, DP_EXPERIMENTAL_PROGRAMME);
  const isESAS = _.isEqual(
    _.get(subjects, "[0].name", ""),
    SUBJECT_NAME_ENVIRONMENTAL_SYSTEMS_SOCIETIES
  );
  // filter fields based on hideForUserTypes config
  if (!_.isEmpty(dynamicTemplate)) {
    if (!getIsSpecifiedConceptsTaggingEnabled()) {
      const addLearningGoalsConfigOptions = _.get(
        dynamicTemplate,
        "field_list.addLearningGoals.config.options",
        []
      );
      const updatedConfigOptions = _.filter(
        addLearningGoalsConfigOptions,
        ({ key }) => key !== "specifiedConcepts"
      );
      _.set(
        dynamicTemplate,
        "field_list.addLearningGoals.config.options",
        updatedConfigOptions
      );
    }
    const step_list = dynamicTemplate["step_list"];
    const field_list = dynamicTemplate["field_list"];

    const view_steps = dynamicTemplate["view_steps"];
    const edit_steps = isFromClassroom
      ? _.get(dynamicTemplate, "classroomEditSteps", [])
      : _.get(dynamicTemplate, "steps", []);

    const steps = mode === "view" ? view_steps : edit_steps;

    if (!_.isEmpty(steps)) {
      _.forEach(steps, step => {
        const stepFields = _.get(step_list, `${step}.fields`, []);
        const filteredStepFields = filterFieldsBasedOnConfig({
          fields: stepFields,
          field_list,
          userType,
        });

        if (step_list?.[step]?.fields) {
          step_list[step].fields = filteredStepFields;
          if (step === "chatbotSettings" && getIsAITutorModalitiesEnabled()) {
            const path = "config.fieldTypeConfig.buttonStyle.minWidth";

            _.forEach(filteredStepFields, field => {
              if (_.has(field_list[field], path)) {
                _.set(field_list[field], path, "344px");
              }
            });
          }
        }
      });
    }
    if (isRoep) {
      const fieldsToRemove = isESAS ? ["ictDetails"] : ["ictDetails_esas"];
      updateStepList({ step_list, fieldsToRemove });

      if (isESAS) {
        const ROEP = _.get(dynamicTemplate, "step_list.ROEP");
        ROEP.subtext = t(
          "unitPlanTemplate:le_DP_EXPERIMENTAL_PROGRAMME_ROEPESAS_subtext"
        );
      }
    }

    if (getIsLearningCoursesEnabled() && getIsNewGradingSetupEnabled()) {
      const standardsField = _.get(field_list, "learningStandardUBD", {});
      if (!_.isEmpty(standardsField)) {
        standardsField.config = {
          ...standardsField.config,
          label: t("common:standard_plural"),
        };
      }
    }

    // in case of AI tutor pypElements/dpElements won't be present
    // but we also do not show rating for learning goals in AI AItutor (enableRating field)
    // so there won't be any issue
    const learningGoalsStep =
      _.get(step_list, "pypElements", _.get(step_list, "dpElements", {})) || {};
    const learningGoalsFields = _.get(learningGoalsStep, "fields", []);

    if (_.includes(learningGoalsFields, "enableRating")) {
      const leHasPypEvaluation = checkAssessmentHasPypEvaluation({
        assessmentFields: resourceFields,
        curriculumProgramType: curriculumType,
        assessmentTools,
      });

      if (!leHasPypEvaluation) {
        const hasTagged = checkHasTaggedLearningGoals({
          assessmentFields: resourceFields,
          curriculumProgramType: curriculumType,
        });
        if (!hasTagged) {
          const fieldStyles = _.get(
            field_list,
            "enableRating.config.styles",
            {}
          );
          field_list.enableRating.config.styles = {
            ...fieldStyles,
            style: {
              ...fieldStyles.style,
              display: "none",
            },
          };
        }
      }
    }

    // Migrating RTE to DOC Editor for worksheets
    const isWorksheet =
      assessmentType === ASSESSMENT_TYPE_WORKSHEET ||
      taskTypeGroup === TASK_TYPE_WORKSHEET;
    const showDocEditor =
      isWorksheet && ACLStore.can("FeatureFlag:WorksheetDocEditorMigration");
    if (showDocEditor) {
      const fieldList = _.get(dynamicTemplate, "field_list", {});

      _.forEach(fieldList, (field, uid) => {
        if (_.get(field, "type") === "RichText") {
          _.set(fieldList, [uid, "type"], "RichText_Ported");
        }
      });
    }

    if (
      sourceFrom === "CLASS_PLANNER" &&
      _.get(field_list, "unitPlan.config") &&
      !getIsLearningCoursesEnabled()
    ) {
      _.set(field_list, "unitPlan.config.isRequired", true);
    }
  }

  return dynamicTemplate;
};

export const getAssessmentSubjects = ({ courseId, learningCourseId }) => {
  if (getIsLearningCoursesEnabled()) {
    const learningCourseDetails = getAssessmentLearningCourseDetailsFromCache({
      id: learningCourseId,
    });

    const learningCourseSubjects = _.get(learningCourseDetails, "subjects", []);

    if (!_.isEmpty(learningCourseSubjects)) {
      return learningCourseSubjects;
    } else {
      return _.get(learningCourseDetails, "primaryGrade.subjects", []);
    }
  }

  const courseDetails = getAssessmentCourseDetailsFromCache({
    id: courseId,
  });
  const courseSubjects = _.get(courseDetails, "subjects", []);

  if (!_.isEmpty(courseSubjects)) {
    return courseSubjects;
  } else {
    return _.get(courseDetails, "primaryGrade.subjects", []);
  }
};

export const getLevelsForSubjectOrCourseBasedSetup = ({ learningCourseId }) => {
  const learningCourseDetails = getAssessmentLearningCourseDetailsFromCache({
    id: learningCourseId,
  });

  if (getIsCourseBasedGradingEnabled()) {
    const associatedLevels = _.get(
      learningCourseDetails,
      "associatedLevels",
      []
    );
    return _.map(associatedLevels, ({ id, label, reportDisplayName }) => ({
      label: reportDisplayName || label,
      value: id,
    }));
  }

  const subjectLevelMap = _.get(learningCourseDetails, "subjectLevelMap", []);
  if (_.isEmpty(subjectLevelMap)) {
    return [];
  }

  // Get all levels from the first subject
  const firstSubjectLevels = _.get(
    _.first(subjectLevelMap),
    "associatedLevels",
    []
  );

  // Find intersection by checking if level exists in all subjects
  const commonLevels = _.filter(firstSubjectLevels, level => {
    return _.every(subjectLevelMap, subject => {
      const associatedLevels = _.get(subject, "associatedLevels", []);
      return _.some(associatedLevels, ({ id }) => id === level.id);
    });
  });

  // Map to required format
  return _.map(commonLevels, ({ id, reportDisplayName, label }) => ({
    label: reportDisplayName || label,
    value: id,
  }));
};

/**
 * We will require planner element type label mapping for now
    as we are mutating template in utils for new learning goals UI.
 * TODO: Once we migrate learning goals to actual templates, we can remove this.
 */
export const plannerElementTypesLabelMapping = {
  // MYP
  atlsMYP: "common:approaches_to_learning_skills",
  learningStandardMYP: "common:subject_standard_label",
  conceptsMYP: "common:concepts_label",
  relatedConceptsMYP: "common:related_concepts",
  specifiedConcepts: "common:specified_concepts",
  lpMYP: "planningInsights:lpa",
  inquiryQuestions: "common:inquiry_questions",
  objectivesMYP: "common:myp_subject_specific_objectives",
  mypNestedElement: "common:intended_learning",
  inquiryQuestionsV2: "common:inquiry_questions",
  interdisciplinaryCriteriaMYP: "common:myp_interdisciplinary_objectives",
  unGoals: "common:sustainable_development_goals",
  unGoalsAssessment: "common:sustainable_development_goals",

  // DP
  learnerProfileDP: "planningInsights:lpa",
  atlDP: "common:approaches_to_learning_skills",
  subjectStandardDP: "common:subject_standard_label",
  syllabusContentDP: "common:syllabus_content_label",
  dpNestedElement: "common:intended_learning",

  // UBD
  learningStandardUBD: "common:content_skills",
  atlsUBD: "common:transdisciplinary_skills",

  // PYP
  atls: "common:approaches_to_learning_skills",
  benchmarks: "common:subject_standard_label",
  lois: "common:lois_label",
  concepts: "common:concepts_label",
  lp: "planningInsights:lpa",
  pypNestedElement: "common:intended_learning",
};

export const getFieldValue = ({ fields, uid }) => {
  const fieldData = _.find(fields, { uid });
  return _.get(fieldData, "value", []) || [];
};

export const getResolvedMinimalTree = ({ fields, uid }) => {
  const fieldData = _.find(fields, { uid });
  return _.get(fieldData, "resolvedMinimalTree", {});
};

export const getResolvedMinimalTreeSet = ({ fields, uid, curriculumType }) => {
  const fieldData = _.find(fields, { uid });

  if (uid === "subjects") {
    return _.get(fieldData, `resolvedMinimalTree.subjects`, []);
  }

  if (uid === "unitPlan") {
    return _.get(fieldData, `resolvedMinimalTree.unitPlan`, {});
  }

  if (uid === "pypNestedElement") {
    return _.get(fieldData, `resolvedMinimalTree.nodes`, []);
  }

  switch (curriculumType) {
    case CURRICULUM_TYPE_PYP:
      return _.get(
        fieldData,
        `resolvedMinimalTree.${pypElementSetMapping[uid]}`,
        []
      );
    default:
      return _.get(fieldData, `resolvedMinimalTree.nodes`, []);
  }
};

export const getSubjectsBasedOnUnitSelection = ({
  unitPlanFields,
  assessmentFields,
  courseId,
  learningCourseId,
}) => {
  const resourceResolvedSubjects = getResolvedMinimalTreeSet({
    fields: assessmentFields,
    uid: "subjects",
  });
  if (!_.isEmpty(resourceResolvedSubjects)) {
    return resourceResolvedSubjects;
  }

  const unitPlanResolvedSubjects = getResolvedMinimalTreeSet({
    fields: unitPlanFields,
    uid: "subjects",
  });
  if (!_.isEmpty(unitPlanResolvedSubjects)) {
    return unitPlanResolvedSubjects;
  }

  return (
    getAssessmentSubjects({
      courseId,
      learningCourseId,
    }) || []
  );
};

export const getInquiryQuestionsNodes = ({
  unitPlanFields,
  assessmentFields,
  fieldKey,
  subFields,
  mode,
}) => {
  const unitPlanFieldValue = getFieldValue({
    fields: unitPlanFields,
    uid: fieldKey,
  });
  const resourceFieldValue = getFieldValue({
    fields: assessmentFields,
    uid: fieldKey,
  });
  const selectedNodes = _.flatten(
    _.map(resourceFieldValue, items => {
      return _.map(items, id => id);
    })
  );

  // filter out empty values
  const filteredUnitPlanFieldValue = _.reduce(
    unitPlanFieldValue,
    (filteredUnitPlanFieldValue, items, key) => {
      return {
        ...filteredUnitPlanFieldValue,
        [key]: _.filter(items, ({ value }) => !_.isEmpty(value)),
      };
    },
    {}
  );

  let value = filteredUnitPlanFieldValue;
  if (mode === "view") {
    value = _.reduce(
      value,
      (selectedValue, items, key) => {
        const selectedItems = _.filter(items, ({ id }) =>
          _.includes(selectedNodes, id)
        );
        return { ...selectedValue, [key]: selectedItems };
      },
      {}
    );
  }

  const nodes = _.reduce(
    value,
    (nodes, items, key) => {
      if (_.isEmpty(items)) {
        return nodes;
      }
      const subFieldConfig = _.get(_.find(subFields, { key }), "config", {});
      const childrenNodes = _.map(items, ({ id, value }) => ({
        id,
        label: value,
        depth: 2,
        children: [],
      }));
      const rootNode = {
        id: key,
        label: _.get(subFieldConfig, "label", ""),
        depth: 1,
        children: childrenNodes,
      };

      return [...nodes, rootNode];
    },
    []
  );

  return { nodes, selectedNodes };
};

const getInquiryQuestionsModifiedSubFields = ({
  subFields,
  unitPlanFieldValue,
  getLearningGoalsTagCountTooltip,
  shouldUpdateActiveQuestionOnClick,
}) => {
  return _.reduce(
    subFields,
    (result, subField) => {
      const { key } = subField;
      const options = [];
      _.forEach(_.get(unitPlanFieldValue, `${key}`, []), option => {
        if (!_.isEmpty(option.value)) {
          options.push({
            label: option.value,
            value: option.id,
            suffixElement: getLearningGoalsTagCountTooltip?.(
              option.id,
              shouldUpdateActiveQuestionOnClick
            ),
          });
        }
      });

      if (!_.isEmpty(options)) {
        result.push({
          ...subField,
          config: {
            ...subField.config,
            defaultOptions: options,
          },
        });
      }

      return result;
    },
    []
  );
};

export const getLinesOfInquiryNodes = ({
  unitPlanFields,
  assessmentFields,
  fieldKey,
  mode,
}) => {
  const unitPlanFieldValue = getFieldValue({
    fields: unitPlanFields,
    uid: fieldKey,
  });
  const resourceFieldValue = getFieldValue({
    fields: assessmentFields,
    uid: fieldKey,
  });

  //filter out empty values
  const filteredUnitPlanFieldValue = _.filter(
    unitPlanFieldValue,
    ({ value }) => !_.isEmpty(value)
  );

  const selectedValue =
    mode !== "view"
      ? filteredUnitPlanFieldValue
      : _.filter(filteredUnitPlanFieldValue, ({ id }) =>
          _.includes(resourceFieldValue, id)
        );

  const nodes = _.map(selectedValue, ({ id, value }) => ({
    id,
    label: value,
    depth: 1,
    children: [],
  }));

  return { nodes, selectedNodes: resourceFieldValue };
};

export const getInquiryQuestionsV2Nodes = ({
  unitPlanFields,
  assessmentFields,
  unitPlanFieldList,
  fieldKey,
  dataField,
  mode,
}) => {
  let unitPlanFieldValue = [];
  if (mode !== "view") {
    unitPlanFieldValue = getDataOptionsForCheckListWithPopup({
      fieldKey,
      dataField,
      unitPlanFieldList,
      unitPlanFields,
    });
  }
  const resourceFieldValue = getFieldValue({
    fields: assessmentFields,
    uid: fieldKey,
  });
  const value = _.map(resourceFieldValue, ({ id }) => id);

  const options = mode == "view" ? resourceFieldValue : unitPlanFieldValue;

  if (_.isEmpty(options)) {
    return { nodes: [], value: [] };
  }

  const tags = _.uniq(_.map(options, ({ tag }) => tag));

  const nodes = _.reduce(
    tags,
    (nodes, tag) => {
      const tagOptions = _.filter(options, { tag });
      if (_.isEmpty(tagOptions)) {
        return nodes;
      }

      const children = _.map(tagOptions, ({ id, value }) => ({
        id,
        label: value,
        depth: 2,
        children: [],
      }));
      const rootNode = {
        id: tag,
        label: tag,
        depth: 1,
        children,
      };
      return [...nodes, rootNode];
    },
    []
  );

  return {
    nodes,
    value,
    unitPlanFieldValue,
  };
};

const getSubjectsWithRecommendedPhase = ({
  subjects,
  subjectRecommendedPhaseMap,
}) => {
  return _.map(subjects, subject => {
    const subjectId = subject.id;
    const recommendedPhase = _.get(subjectRecommendedPhaseMap, subjectId, []);

    return { ...subject, recommendedPhase };
  });
};

export const getNodesWithGradeLevels = ({ nodes }) => {
  return _.map(nodes, node => {
    const grades = getNodeParents(node, "GRADE");
    return {
      ...node,
      grades,
    };
  });
};

export const getLearningGoalsData = ({
  fieldKey,
  plannerElementType,
  unitPlanFields,
  assessmentFields,
  subFields = [],
  curriculumType,
  mode = "edit",
  plannerElementSubType,
  plannerElementNodes = [],
  courseId,
  dataField,
  unitPlanFieldList,
  fieldValue,
  shouldConsiderFieldValue,
  fieldObjConfigOptions,
  isPlannerElement = false,
  getLearningGoalsTagCountTooltip,
  shouldUpdateActiveQuestionOnClick = false,
  subjectRecommendedPhaseMap,
  learningCourseId,
}) => {
  // from assessment allFields data
  const resourceFieldValue = shouldConsiderFieldValue
    ? fieldValue
    : getFieldValue({
        fields: assessmentFields,
        uid: fieldKey,
      });
  const resourceNodes = getResolvedMinimalTreeSet({
    uid: fieldKey,
    fields: assessmentFields,
    curriculumType,
  });

  // from unitPlan allFields data
  const unitPlanFieldValue = getFieldValue({
    fields: unitPlanFields,
    uid: fieldKey,
  });
  const unitPlanNodes = getResolvedMinimalTreeSet({
    uid: fieldKey,
    fields: unitPlanFields,
    curriculumType,
  });

  const isStandardsField = STANDARD_UIDS[curriculumType] === fieldKey;

  // combine all value and nodes in edit mode when tagging all learning goals
  const plannerElementValue = getPlannerElementValue({
    nodes: plannerElementNodes,
    isStandardsField,
  });
  const allValue = isPlannerElement
    ? _.uniq([
        ...(plannerElementValue || []),
        ...(unitPlanFieldValue || []),
        ...(resourceFieldValue || []),
      ])
    : [];
  const allNodes = isPlannerElement
    ? _.uniqBy(
        [
          ...(plannerElementNodes || []),
          ...(unitPlanNodes || []),
          ...(resourceNodes || []),
        ],
        "id"
      )
    : [];

  switch (plannerElementType) {
    // subject dependent planner element types
    case ELEMENT_TYPE_MYP_LEARNING_STANDARD:
    case ELEMENT_TYPE_DP_LEARNING_STANDARD:
    case ELEMENT_TYPE_UBD_LEARNING_STANDARD:
    case ELEMENT_TYPE_DP_SYLLABUS:
    case ELEMENT_TYPE_MYP_RELATED_CONCEPT:
    case ELEMENT_TYPE_MYP_OBJECTIVES: {
      const resourceNodeSet =
        getPYPElementSetFromNodes({ nodes: resourceNodes }) || {};

      // For non-pyp curriculum, grades are there in associatedParents of nodes
      // So extract them and add to the node explicitly to show in DataGrid UI grade levels column
      const filteredUnitPlanNodes = getNodesWithGradeLevels({
        nodes: unitPlanNodes,
      });
      const filteredAllNodes = getNodesWithGradeLevels({
        nodes: allNodes,
      });

      const unitPlanNodeSet =
        getPYPElementSetFromNodes({ nodes: filteredUnitPlanNodes }) || {};
      const allNodeSet =
        getPYPElementSetFromNodes({ nodes: filteredAllNodes }) || {};

      if (!_.isEmpty(plannerElementSubType)) {
        // case for interdisciplinaryCriteriaMYP
        if (mode === "view") {
          return {
            ...resourceNodeSet,
            value: resourceFieldValue,
          };
        }

        return {
          value: resourceFieldValue,
          unitSpecificData: {
            ...unitPlanNodeSet,
            value: unitPlanFieldValue,
          },
          globalData: {
            ...allNodeSet,
            value: allValue,
          },
        };
      }

      const parentNodes = getSubjectsBasedOnUnitSelection({
        unitPlanFields,
        assessmentFields,
        courseId,
        curriculumType,
        learningCourseId,
      });

      if (mode === "view") {
        return {
          ...resourceNodeSet,
          value: resourceFieldValue,
          parentNodes,
          groupByType: "SUBJECT",
        };
      }

      const parentNodesWithRecommendedPhase = getSubjectsWithRecommendedPhase({
        subjects: parentNodes,
        subjectRecommendedPhaseMap,
      });

      return {
        value: resourceFieldValue,
        unitSpecificData: {
          ...unitPlanNodeSet,
          value: unitPlanFieldValue,
          parentNodes: parentNodesWithRecommendedPhase,
        },
        globalData: {
          ...allNodeSet,
          value: allValue,
          parentNodes: parentNodesWithRecommendedPhase,
        },
        groupByType: "SUBJECT",
      };
    }

    // pyp benchmarks (dependent on subjects)
    case ELEMENT_TYPE_PYP_LEARNING_STANDARD: {
      const resourceNodeSet =
        getPYPElementSetFromNodes({
          nodes: _.filter(resourceNodes, ({ depth }) => depth > 0),
          startDepth: 1,
        }) || {};
      const unitPlanNodeSet =
        getPYPElementSetFromNodes({
          nodes: _.filter(unitPlanNodes, ({ depth }) => depth > 0),
          startDepth: 1,
        }) || {};
      const allNodeSet =
        getPYPElementSetFromNodes({
          nodes: _.filter(allNodes, ({ depth }) => depth > 0),
          startDepth: 1,
        }) || {};

      const parentNodes = getSubjectsBasedOnUnitSelection({
        unitPlanFields,
        assessmentFields,
        courseId,
        curriculumType,
        learningCourseId,
      });

      if (mode === "view") {
        return {
          ...resourceNodeSet,
          value: resourceFieldValue,
          parentNodes,
          groupByType: "SUBJECT",
          isSubjectPropInNode: true,
        };
      }

      return {
        value: resourceFieldValue,
        unitSpecificData: {
          ...unitPlanNodeSet,
          value: unitPlanFieldValue,
          parentNodes,
        },
        globalData: {
          ...allNodeSet,
          value: allValue,
          parentNodes,
        },
        groupByType: "SUBJECT",
        isSubjectPropInNode: true,
        allNodes,
      };
    }

    // grade dependent planner element types
    case ELEMENT_TYPE_PYP_ATL:
    case ELEMENT_TYPE_MYP_ATL:
    case ELEMENT_TYPE_DP_ATL:
    case ELEMENT_TYPE_UBD_ATL:
    case PLANNER_ELEMENT_NESTED_CHECKLIST: {
      const resourceNodeSet =
        getPYPElementSetFromNodes({ nodes: resourceNodes }) || {};
      const unitPlanNodeSet =
        getPYPElementSetFromNodes({ nodes: unitPlanNodes }) || {};
      const allNodeSet = getPYPElementSetFromNodes({ nodes: allNodes }) || {};

      if (mode === "view") {
        return {
          ...resourceNodeSet,
          value: resourceFieldValue,
        };
      }

      return {
        value: resourceFieldValue,
        unitSpecificData: {
          ...unitPlanNodeSet,
          value: unitPlanFieldValue,
          parentNodes: _.map(
            _.get(unitPlanNodeSet, "rootNodes", []),
            id => _.find(unitPlanNodes, { id }) || {}
          ),
        },
        globalData: {
          ...allNodeSet,
          value: allValue,
          parentNodes: _.map(
            _.get(allNodeSet, "rootNodes", []),
            id => _.find(allNodes, { id }) || {}
          ),
        },
        groupByType: "PLANNER_ELEMENT",
      };
    }

    // independent planner element types (neither dependent on grades nor subjects)
    case ELEMENT_TYPE_PYP_LEARNER_PROFILE:
    case ELEMENT_TYPE_MYP_LEARNER_PROFILE:
    case ELEMENT_TYPE_DP_LEARNER_PROFILE:
    case ELEMENT_TYPE_PYP_KEY_CONCEPT:
    case ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT:
    case ELEMENT_TYPE_MYP_KEY_CONCEPT: {
      if (mode === "view") {
        return {
          value: resourceFieldValue,
          nodes: resourceNodes,
        };
      }

      return {
        value: resourceFieldValue,
        unitSpecificData: {
          value: unitPlanFieldValue,
          nodes: unitPlanNodes,
        },
        globalData: {
          value: allValue,
          nodes: allNodes,
        },
      };
    }

    default: {
      switch (fieldKey) {
        case TEMPLATE_FIELD_LINES_OF_INQUIRY: {
          if (mode === "view") {
            return {
              value: _.map(resourceFieldValue, id => {
                const node = _.find(unitPlanFieldValue, { id }) || {};
                return { label: _.get(node, "value", ""), value: id };
              }),
            };
          }

          const filteredUnitPlanFieldValue = _.filter(
            unitPlanFieldValue,
            ({ value }) => !_.isEmpty(value)
          );

          const defaultOptions = _.map(
            filteredUnitPlanFieldValue,
            ({ id, value }) => ({
              label: value,
              value: id,
              suffixElement: getLearningGoalsTagCountTooltip?.(
                id,
                shouldUpdateActiveQuestionOnClick
              ),
            })
          );

          return {
            value: resourceFieldValue,
            defaultOptions,
          };
        }

        case TEMPLATE_FIELD_INQUIRY_QUESTIONS: {
          if (mode === "view") {
            const { nodes } = getInquiryQuestionsNodes({
              unitPlanFields,
              assessmentFields,
              fieldKey,
              subFields,
              mode,
            });

            return {
              value: resourceFieldValue,
              nodes,
            };
          }

          return {
            value: !_.isEmpty(resourceFieldValue) ? resourceFieldValue : {},
            subFields: getInquiryQuestionsModifiedSubFields({
              subFields,
              unitPlanFieldValue,
              getLearningGoalsTagCountTooltip,
            }),
          };
        }

        case TEMPLATE_FIELD_INQUIRY_QUESTIONS_V2: {
          const {
            nodes,
            value,
            unitPlanFieldValue,
          } = getInquiryQuestionsV2Nodes({
            unitPlanFields,
            assessmentFields,
            unitPlanFieldList,
            fieldKey,
            dataField,
            mode,
          });

          if (mode === "view") {
            return {
              value,
              nodes,
            };
          }

          return {
            value,
            subFields,
            unitSpecificData: {
              value: unitPlanFieldValue,
              nodes,
            },
          };
        }

        case TEMPLATE_FIELD_UN_GOALS:
        case TEMPLATE_FIELD_UN_GOALS_ASSESSMENT: {
          if (mode === "view") {
            return {
              value: getPlannerElementNodesFromFieldOptions({
                fieldObjConfigOptions,
                resourceFieldValue,
                mode: "view",
              }),
            };
          }

          return {
            value: resourceFieldValue,
            unitSpecificData: {
              value: unitPlanFieldValue,
              nodes: getPlannerElementNodesFromFieldOptions({
                fieldObjConfigOptions,
                resourceFieldValue,
                mode: "edit",
              }),
            },
          };
        }
      }
    }
  }
};

export const getLearningGoalsDataMemoize = _.memoize(
  params => getLearningGoalsData(params),
  params => JSON.stringify(params)
);

export const getUnitDetailsFromUnitPlanField = ({ assessmentDetails }) => {
  const allFields = _.get(assessmentDetails, "allFields", []);
  const unitPlanField = _.find(allFields, { uid: "unitPlan" });
  const unitPlanId = _.get(unitPlanField, "value");
  const grades = _.get(
    unitPlanField,
    "resolvedMinimalTree.unitPlan.grades",
    ""
  );

  const unitPlanResolvedMinimalTree = _.get(
    unitPlanField,
    "resolvedMinimalTree.unitPlan",
    {}
  );

  const unitPlanFields = _.get(unitPlanResolvedMinimalTree, "allFields", []);

  return {
    unitPlanId,
    grades,
    unitPlanFields,
    unitPlanResolvedMinimalTree,
  };
};

export const getPlannerElementValue = ({ nodes, isStandardsField }) => {
  if (
    isStandardsField &&
    ACLStore.can("FeatureFlag:EnableParentStandardsTagging")
  )
    return _.map(nodes, ({ id }) => id);

  const leafNodes = _.filter(nodes, ({ children }) => _.isEmpty(children));
  return _.map(leafNodes, ({ id }) => id);
};

export const getGradeIdsBasedOnUnitSelection = ({
  assessmentFields,
  courseId,
  course,
  learningCourseId,
}) => {
  const selectedUnitPlan = getResolvedMinimalTreeSet({
    fields: assessmentFields,
    uid: "unitPlan",
  });

  if (!_.isEmpty(selectedUnitPlan)) {
    return _.get(selectedUnitPlan, "grades", []);
  }

  // handles places where AddAssignment is not used (e.g unit plan)
  if (!_.isEmpty(course)) {
    return _.map(course?.grades, "id");
  }

  if (getIsLearningCoursesEnabled()) {
    const learningCourseDetails = getAssessmentLearningCourseDetailsFromCache({
      id: learningCourseId,
    });
    return _.map(_.get(learningCourseDetails, "grades", []), ({ id }) => id);
  }

  const courseDetails = getAssessmentCourseDetailsFromCache({
    id: courseId,
  });

  return _.map(_.get(courseDetails, "grades", []), ({ id }) => id);
};

export const getPlannerElementNodesQueryVariables = ({
  plannerElementType,
  assessmentFields,
  unitPlanFields,
  courseId,
  curriculumProgram,
  isStepWithPlannerElement,
  skipQuery,
  course,
  plannerElementSubType,
  learningCourseId,
}) => {
  const {
    id: curriculumId,
    nodeInterfaceType: curriculumNodeInterfaceType,
    type: curriculumType,
  } = curriculumProgram;

  const isStandardsV2Enabled = ACLStore.can("AdminPortal:StandardsV2");

  const { orderBy, orderByDirection } = getDatasetsOrderByArgs();

  switch (plannerElementType) {
    case ELEMENT_TYPE_MYP_LEARNING_STANDARD:
    case ELEMENT_TYPE_DP_LEARNING_STANDARD:
    case ELEMENT_TYPE_UBD_LEARNING_STANDARD: {
      const resolvedSubjects = getSubjectsBasedOnUnitSelection({
        unitPlanFields,
        assessmentFields,
        courseId,
        curriculumType,
        learningCourseId,
      });
      const subjectIds = _.map(resolvedSubjects, ({ id }) => id);

      return {
        plannerElementType,
        plannerElementTypes: [plannerElementType],
        curriculumId,
        curriculumNodeInterfaceType,
        curriculumType,
        filters: {
          associatedParents: [{ ids: subjectIds, type: "SUBJECT" }],
        },
        plannerElementNodeFilters: {
          subjects: subjectIds,
        },
        plannerElementNodeSetFilters: {
          subjects: subjectIds,
        },
        isStepWithPlannerElement,
        skipQuery,
        isStandardsV2Enabled,
        orderBy,
        orderByDirection,
      };
    }

    case ELEMENT_TYPE_DP_SYLLABUS:
    case ELEMENT_TYPE_MYP_RELATED_CONCEPT: {
      const resolvedSubjects = getSubjectsBasedOnUnitSelection({
        unitPlanFields,
        assessmentFields,
        courseId,
        curriculumType,
        learningCourseId,
      });
      const subjectIds = _.map(resolvedSubjects, ({ id }) => id);

      return {
        plannerElementTypes: [plannerElementType],
        curriculumId,
        curriculumNodeInterfaceType,
        curriculumType,
        filters: {
          associatedParents: [{ ids: subjectIds, type: "SUBJECT" }],
        },
        isStepWithPlannerElement,
        skipQuery,
      };
    }

    case ELEMENT_TYPE_MYP_OBJECTIVES: {
      if (!_.isEmpty(plannerElementSubType)) {
        return {
          plannerElementTypes: [plannerElementType],
          curriculumId,
          curriculumNodeInterfaceType,
          curriculumType,
          filters: { subTypes: [plannerElementSubType] },
          isStepWithPlannerElement,
          skipQuery,
        };
      }

      const resolvedSubjects = getSubjectsBasedOnUnitSelection({
        unitPlanFields,
        assessmentFields,
        courseId,
        curriculumType,
        learningCourseId,
      });
      const subjectIds = _.map(resolvedSubjects, ({ id }) => id);

      return {
        plannerElementTypes: [plannerElementType],
        curriculumId,
        curriculumNodeInterfaceType,
        curriculumType,
        filters: {
          associatedParents: [{ ids: subjectIds, type: "SUBJECT" }],
        },
        isStepWithPlannerElement,
        skipQuery,
      };
    }

    case ELEMENT_TYPE_MYP_ATL:
    case ELEMENT_TYPE_DP_ATL:
    case ELEMENT_TYPE_UBD_ATL: {
      const grades = getGradeIdsBasedOnUnitSelection({
        assessmentFields,
        courseId,
        course,
        learningCourseId,
      });

      return {
        plannerElementTypes: [plannerElementType],
        curriculumId,
        curriculumNodeInterfaceType,
        curriculumType,
        filters: {
          associatedParents: [{ ids: grades, type: "GRADE" }],
        },
        isStepWithPlannerElement,
        skipQuery,
      };
    }

    case ELEMENT_TYPE_PYP_LEARNER_PROFILE:
    case ELEMENT_TYPE_MYP_LEARNER_PROFILE:
    case ELEMENT_TYPE_DP_LEARNER_PROFILE:
    case ELEMENT_TYPE_PYP_KEY_CONCEPT:
    case ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT:
    case ELEMENT_TYPE_MYP_KEY_CONCEPT: {
      return {
        plannerElementTypes: [plannerElementType],
        curriculumId,
        curriculumNodeInterfaceType,
        curriculumType,
        isStepWithPlannerElement,
        skipQuery,
      };
    }

    case ELEMENT_TYPE_PYP_ATL: {
      const grades = getGradeIdsBasedOnUnitSelection({
        assessmentFields,
        courseId,
        course,
        learningCourseId,
      });

      return {
        plannerElementTypes: [plannerElementType],
        curriculumId,
        curriculumType,
        grades,
        skipQuery,
      };
    }

    case ELEMENT_TYPE_PYP_LEARNING_STANDARD: {
      const resolvedSubjects = getSubjectsBasedOnUnitSelection({
        unitPlanFields,
        assessmentFields,
        courseId,
        curriculumType,
        learningCourseId,
      });
      const subjectIds = _.map(resolvedSubjects, ({ id }) => id);

      return {
        plannerElementType: ELEMENT_TYPE_PYP_LEARNING_STANDARD,
        curriculumId,
        curriculumType,
        subjects: subjectIds,
        skipQuery,
        plannerElementNodeFilters: {
          subjects: subjectIds,
        },
        plannerElementNodeSetFilters: {
          subjects: subjectIds,
        },
        orderBy,
        orderByDirection,
        isStandardsV2Enabled,
      };
    }
    case PLANNER_ELEMENT_NESTED_CHECKLIST: {
      const grades = getGradeIdsBasedOnUnitSelection({
        assessmentFields,
        courseId,
        course,
        learningCourseId,
      });

      return {
        plannerElementTypes: [plannerElementType],
        curriculumId,
        curriculumNodeInterfaceType,
        curriculumType,
        filters: {
          associatedParents: [{ ids: grades, type: "GRADE" }],
        },
        isStepWithPlannerElement,
        skipQuery,
      };
    }
  }
};

export const getPopupDataForSubFields = ({
  unitPlanFieldList,
  fieldKey,
  unitPlanFields,
  dataField,
  subFields,
}) => {
  const unitPlanFieldObj = unitPlanFieldList[fieldKey];
  const unitPlanObjSubFields = _.get(unitPlanFieldObj, "subFields", []);

  const options = {};

  _.forEach(unitPlanObjSubFields, ({ config, key }) => {
    const filteredBy = _.get(config, "filteredBy", []);

    let resolvedMinimalTree = {};
    _.forEach(filteredBy, fieldUid => {
      resolvedMinimalTree = _.get(
        _.find(unitPlanFields, ({ uid }) => uid === fieldUid),
        "resolvedMinimalTree",
        {}
      );
    });
    options[key] = resolvedMinimalTree;
  });

  return {
    popupData: getPopupDataForCheckListWithPopupMemoized({
      unitPlanFields,
      fieldKey,
      subFields,
      unitPlanFieldList,
      options,
      dataField,
    }),
    options,
  };
};

export const getUnitPlanFieldsFromAssessment = ({
  assessmentDetails,
  type = "allFields",
}) => {
  const allFields = _.get(assessmentDetails, type, []);
  const unitPlanField = _.find(allFields, { uid: "unitPlan" }) || {};
  return _.get(unitPlanField, "resolvedMinimalTree.unitPlan.allFields", []);
};

export const getUnitPlanFromAssessment = ({ assessmentDetails }) => {
  const allFields = _.get(assessmentDetails, "allFields", []);
  const unitPlanField = _.find(allFields, { uid: "unitPlan" }) || {};
  return _.get(unitPlanField, "resolvedMinimalTree.unitPlan", {});
};

export const getAssessmentLocalStorageKey = params => {
  const { courseId, creationMode = "ASSESSMENT" } = params || {};
  return `COURSE_${courseId}_${creationMode}`;
};

export const setAssessmentInLocalStorage = params => {
  const { value, courseId } = params;

  // If courseId is empty do not store assessment in local storage (case for quick task creation from calendar)
  if (_.isEmpty(courseId)) {
    return;
  }

  const item = {
    key: getAssessmentLocalStorageKey({ ...params, ...value }),
    value,
  };

  setEncryptedJsonItem(item);
};

export const getAssessmentFromLocalStorage = params => {
  const encryptedValue = localStorage.getItem(
    getAssessmentLocalStorageKey(params)
  );
  if (_.isEmpty(encryptedValue)) {
    return JSON.stringify({});
  }
  return AESDecrypt(encryptedValue);
};

export const removeAssessmentFromLocalStorage = params => {
  localStorage.removeItem(getAssessmentLocalStorageKey(params));
};

export const isGenericTemplate = templateId => {
  return GENERIC_TEMPLATES.includes(templateId);
};

export const combineUnitPlanMultiFieldData = ({
  templateBody,
  unitPlanFields,
}) => {
  const { field_list } = templateBody || {};

  if (!field_list) {
    return unitPlanFields;
  }

  const fieldKeys = _.keys(field_list);

  _.forEach(fieldKeys, fieldKey => {
    const fieldObj = field_list[fieldKey];
    const { type } = fieldObj;

    // LearningGoalsView is added temporarily, will be removed
    if (_.includes(["MultiField", "LearningGoalsNestedView"], type)) {
      const individualUnitPlanFields = _.filter(unitPlanFields, ({ uid }) =>
        _.startsWith(uid, fieldKey + "_")
      );
      const areFieldValuesNull = _.every(
        individualUnitPlanFields,
        ({ value }) => value === null
      );
      if (!_.isEmpty(individualUnitPlanFields) && !areFieldValuesNull) {
        const combinedValues = _.reduce(
          individualUnitPlanFields,
          (result, { uid, value }) => ({
            ...result,
            [_.split(uid, "_")[1]]: value,
          }),
          {}
        );
        if (!_.isEmpty(_.find(unitPlanFields, { uid: fieldKey }))) {
          const multiFieldIndex = _.indexOf(
            unitPlanFields,
            _.find(unitPlanFields, { uid: fieldKey })
          );
          if (multiFieldIndex !== -1) {
            unitPlanFields = update(unitPlanFields, {
              [multiFieldIndex]: {
                value: { $set: combinedValues },
              },
            });
          }
        } else {
          unitPlanFields.push({
            id: generateRandomIdV2(),
            uid: fieldKey,
            value: combinedValues,
          });
        }
      }

      unitPlanFields = _.differenceBy(
        unitPlanFields,
        individualUnitPlanFields,
        ({ uid }) => uid
      );
    }
  });

  return unitPlanFields;
};

/**
 * This function will return labels of learning goals based on following condition
 * If generic template -> returns fixed label defined in plannerElementTypesLabelMapping
 * If custom template -> returns viewLabel
 */
export const getPlannerElementLabel = ({
  t,
  field,
  templateId,
  viewLabel,
  viewLabelLocale,
  plannerElementSets = [],
  curriculumType,
  plannerElementType,
}) => {
  let genericLabel = _.get(plannerElementTypesLabelMapping, field);
  genericLabel = genericLabel ? t(genericLabel) : "";

  if (
    curriculumType === "UBD" &&
    !_.isEmpty(plannerElementSets) &&
    viewLabelLocale
  ) {
    return t("common:capitalize", {
      text: t(viewLabelLocale, {
        label: plannerElementSets[plannerElementType]?.label,
      }),
    });
  }

  if (genericLabel && isGenericTemplate(templateId)) {
    return genericLabel;
  }

  return viewLabel || genericLabel;
};

export const getUnitPlanIdFromAssessment = ({ assessmentDetails }) => {
  const allFields = _.get(assessmentDetails, "allFields", []);
  const specificFields = _.get(assessmentDetails, "fields", []);

  const unitPlan =
    _.find(allFields, { uid: "unitPlan" }) ||
    _.find(specificFields, { uid: "unitPlan" }) ||
    {};

  return _.get(unitPlan, "value");
};

const flattenChildrendNodes = ({ node, flattenedNodes }) => {
  flattenedNodes.push(node);
  if (node.children) {
    _.forEach(node.children, childNode => {
      if (childNode) {
        flattenChildrendNodes({ node: childNode, flattenedNodes });
      }
    });
  }
};

export const getFlattenedNodes = ({ nodes }) => {
  const flattenedNodes = [];
  _.forEach(nodes, node => {
    if (node) {
      flattenChildrendNodes({ node, flattenedNodes });
    }
  });
  return flattenedNodes;
};

export const getSideNavigationOptions = getMemoizedFunction(
  ({ parentNodes }) => {
    const rootOption = {
      key: "root",
      children: _.map(parentNodes, ({ id }) => id),
    };

    const options = _.map(parentNodes, ({ id, name, label }) => ({
      key: id,
      title: name || label,
      tier: "1",
      parent: "root",
      children: [],
    }));

    return [rootOption, ...options];
  }
);

export const getIsUnitPlanFieldDisabled = params => {
  const {
    creationMode,
    sourceFrom,
    assessmentMode,
    value,
    isFromUnitPlan = false,
    isDraft = false,
    preSelectedUnitPlan,
  } = params;

  if (isDraft) {
    return false;
  }

  if (
    !_.isEmpty(value) &&
    assessmentMode === "edit" &&
    !getIsUnitPlanUpdateModalEnabled()
  ) {
    return true;
  }

  const isImportFromUnit = _.includes(["IMPORT_LE", "UNIT"], creationMode);

  return (
    isFromUnitPlan ||
    _.isNil(sourceFrom) ||
    sourceFrom === "UNIT_PLAN" ||
    isImportFromUnit ||
    (getIsAssignmentsUnitTaggingIn2_0Enabled() &&
      !_.isEmpty(preSelectedUnitPlan))
  );
};

// TODO - remove as soon as all templates with learning goals related changes are updated from BE
export const updateTemplateForLearningGoals = ({ template, templateId }) => {
  const getLabel = ({ field, templateId, viewLabel }) => {
    const genericLabel = _.get(plannerElementTypesLabelMapping, field, "");

    if (isGenericTemplate(templateId)) {
      return genericLabel;
    }

    return viewLabel || genericLabel;
  };

  const step_list = template.step_list;
  const field_list = template.field_list;

  const learningGoalsStep =
    _.get(step_list, "pypElements") || _.get(step_list, "dpElements") || {};

  if (_.isEmpty(learningGoalsStep)) {
    return template;
  }

  const pypElementFields = _.get(learningGoalsStep, "fields", []);

  if (_.includes(pypElementFields, "addLearningGoals")) {
    return template;
  }

  const filteredPypElementsFields = _.filter(
    pypElementFields,
    field => field != "enableRating"
  );

  const dropdownOptions = _.map(filteredPypElementsFields, field => {
    const fieldObj = _.get(field_list, `${field}`, {});
    const config = _.get(fieldObj, "config", {});
    const viewLabel = _.get(config, "viewLabel", "");

    return {
      key: field,
      label: getLabel({
        field,
        templateId,
        viewLabel,
      }),
    };
  });

  if (_.includes(pypElementFields, "enableRating")) {
    filteredPypElementsFields.push("enableRating");
  }

  _.forEach(pypElementFields, field => {
    let fieldObj = _.get(field_list, field, {});

    switch (field) {
      case "enableRating":
        fieldObj = {
          ..._.get(field_list, field, {}),
          viewType: "LearningGoalsRating",
          config: {
            ..._.get(field_list, `${field}.config`, {}),
            styles: {
              style: {
                ..._.get(field_list, `${field}.config.styles.style`, {}),
                padding: "12px 0 0",
              },
            },
            icon: "UserProfileOutlined",
            label: "Rate on the learning goals tagged above",
          },
        };
        break;

      default: {
        const config = _.get(fieldObj, "config", {});
        const plannerElementLabel = getLabel({
          field,
          templateId,
          viewLabel: _.get(config, "viewLabel", ""),
        });

        const otherConfigOptions = {};
        let type = "TreeView";
        let viewType = "LearningGoalsNestedView";
        switch (field) {
          case "inquiryQuestions":
            otherConfigOptions.showSearchFilter = false;
            type = "MultiField";
            break;
          case "inquiryQuestionsV2":
            otherConfigOptions.showPopupForSubFields = true;
            otherConfigOptions.showSearchFilter = false;
            break;
          case "objectivesMYP":
            otherConfigOptions.showRadioButton = true;
            break;
          case "interdisciplinaryCriteriaMYP":
            otherConfigOptions.showSideNavigation = false;
            break;
          case "lois":
            type = "CheckList";
            viewType = "LearningGoalsListView";
            break;
          case "lp":
          case "concepts":
          case "conceptsMYP":
          case "lpMYP":
          case "learnerProfileDP":
          case "unGoals":
          case "unGoalsAssessment":
            type = "CardView";
            viewType = "LearningGoalsListView";
            break;
        }

        fieldObj = {
          ..._.get(field_list, field, {}),
          config: {
            ..._.get(field_list, `${field}.config`, {}),
            fieldTypeConfig: {
              ..._.get(field_list, `${field}.config.fieldTypeConfig`, {}),
              labelStyle: {
                fontSize: "14px",
              },
            },
            styles: {
              style: {
                padding: "12px 0 0",
              },
            },
            label: plannerElementLabel,
            viewLabel: plannerElementLabel,
            renderFieldIfEmpty: false,
            ...otherConfigOptions,
          },
          type,
          viewType,
        };

        // add plannerElementConfig -> type for pyp le templates
        if (_.includes(_.keys(fieldPlannerElementTypeMappingForPyp), field)) {
          fieldObj.config.plannerElementConfig = {
            type: fieldPlannerElementTypeMappingForPyp[field],
          };
          fieldObj.config.type = "PYP_PLANNER_ELEMENT";
        }
      }
    }

    field_list[field] = fieldObj;
  });

  const addLearningGoalsField = {
    config: {
      fieldTypeConfig: {
        labelContainerStyle: {
          margin: "0px",
        },
      },
      styles: {
        style: {
          padding: "0px",
        },
      },
      label: _.get(learningGoalsStep, "label") || "Learning goals",
      subtext: !isGenericTemplate(templateId)
        ? _.get(learningGoalsStep, "subtext", "")
        : "",
      hasAddRemoveValues: true,
      options: dropdownOptions,
      icon: "GoalsOutlined",
    },
    type: "AddLearningGoals",
    viewType: "AddLearningGoals",
    printType: "LearningGoals",
  };

  learningGoalsStep.fields = ["addLearningGoals", ...filteredPypElementsFields];
  field_list.addLearningGoals = addLearningGoalsField;

  const learningGoalsStepName = step_list?.pypElements
    ? "pypElements"
    : "dpElements";

  if (_.has(step_list, learningGoalsStepName)) {
    template = update(template, {
      step_list: {
        [learningGoalsStepName]: {
          $unset: ["label", "viewLabel"],
        },
      },
    });
  }

  return template;
};

const getStepFields = params => {
  const { steps = [], template = {} } = params;
  return _.reduce(
    steps,
    (result, step) => {
      const stepFields = _.get(template, `step_list.${step}.fields`, []);

      const fieldOptions = _.reduce(
        stepFields,
        (result, field) => {
          const options = _.get(template, `field_list.${field}.config.options`);
          const optionKeys = _.compact(_.map(options, "key"));
          if (!_.isEmpty(optionKeys)) {
            result.push(...optionKeys);
          }

          return result;
        },
        []
      );

      result.push(...stepFields);
      result.push(...fieldOptions);
      return result;
    },
    []
  );
};

export const getModifiedContextFields = prompts => {
  return _.map(prompts, prompt => {
    const { type } = prompt;

    if (_.includes(type, "_")) {
      const [fieldType, fieldUID] = _.split(type, "_");
      return {
        ...prompt,
        field: fieldUID,
        type: fieldType,
      };
    }

    return prompt;
  });
};

export const updateCustomLabelsForUBDLearningGoals = ({
  field_list,
  curriculumType,
  plannerElementSets,
  t,
}) => {
  if (_.includes(["UBD"], curriculumType) && !_.isEmpty(plannerElementSets)) {
    _.forEach(field_list, (field, key) => {
      if (_.get(field, "config.type", "") == "PLANNER_ELEMENT") {
        const label =
          plannerElementSets[
            _.get(field, "config.plannerElementConfig.type", "")
          ]?.label;

        field_list[key] = {
          ...field,
          skipLocalisation: true,
          config: {
            ...field.config,
            label: field.config?.viewLabelLocale
              ? t("common:capitalize", {
                  text: t(field.config.viewLabelLocale, { label }),
                })
              : "",
            subtext: field.config?.subTextLocale
              ? t(field.config.subTextLocale, { label })
              : "",
            viewLabel: field.config?.viewLabelLocale
              ? t("common:capitalize", {
                  text: t(field.config.viewLabelLocale, { label }),
                })
              : "",
            emptyText: field.config?.emptyTextLocale
              ? t(field.config.emptyTextLocale, { label })
              : "",
            emptyViewText: field.config?.emptyViewTextLocale
              ? t(field.config.emptyViewTextLocale, { label })
              : "",
            fieldTypeConfig: {
              ...field.config.fieldTypeConfig,
              plannerLabel: label,
              emptyText: field.config?.fieldTypeConfig?.emptyTextLocale
                ? t(field.config.fieldTypeConfig.emptyTextLocale, { label })
                : "",
              emptyParentNodeText: field.config?.fieldTypeConfig
                ?.emptyParentNodeTextLocale
                ? t(field.config.fieldTypeConfig.emptyParentNodeTextLocale, {
                    label,
                  })
                : "",
            },
          },
        };
      }
    });
  }
};

export const updateDynamicLabelsForPlannerElements = ({
  field_list,
  plannerElementSets,
}) => {
  const isNewGradingSetupEnabled = getIsNewGradingSetupEnabled();
  if (
    !isNewGradingSetupEnabled ||
    _.isEmpty(plannerElementSets) ||
    _.isEmpty(field_list)
  )
    return;

  _.forEach(FIELD_KEY_TO_PLANNER_ELEMENT_TYPE, (elementType, fieldKey) => {
    const field = field_list[fieldKey];
    if (!field) return;

    const customLabel = _.get(plannerElementSets, `${elementType}.label`, "");
    if (!customLabel) return;

    _.set(field, "config.label", customLabel);
    _.set(field, "config.viewLabel", customLabel);
  });
};

/**
 *
 * @param {*}
 * @description
 * Updating Weightage field to TaskWeightV3
 */
const updateFieldForTaskWeightageV3 = ({ field_list }) => {
  const field = _.get(field_list, "weightage", {});

  // * Removing subtext and updating style as per revamped weightage field
  _.unset(field, "config.subtext");
  _.set(field, "config.fieldTypeConfig.containerStyle", {
    alignItems: "center",
    flexDirection: "row",
  });
  _.set(field, "config.fieldTypeConfig.labelContainerStyle", {
    margin: "0px",
    maxWidth: "max-content",
  });
  _.set(field, "config.fieldTypeConfig.editContainerStyle", {
    justifyContent: "flex-start",
  });

  // * Adding new field TaskWeightV3 for Revamped Weightage field
  _.set(field, "type", "TaskWeightV3");
  _.set(field, "viewType", "TaskWeightV3");
};

const _getDynamicTemplate = ({
  template,
  unitType,
  t,
  curriculumType,
  plannerElementSets,
  subjects,
  courseSubjects,
  curriculumId,
  templateId,
  organizationId,
  ltiDropDownOptions,
  isFromInitializingModal,
  isAIEvaluationEnabled,
  isAIEvaluationExpanded,
  showTaskWeightageV3,
  isCustomWeightsV2,
  isUsingNewGradingSetupInCourses,
  shouldRemoveCoverImage,
  learningCourseId,
  courseId,
}) => {
  const dynamicTemplate = updateTemplateForLearningGoals({
    template: _.cloneDeep(template),
    templateId,
  });

  const step_list = dynamicTemplate["step_list"];
  const field_list = dynamicTemplate["field_list"];
  if (_.includes(["UBD"], curriculumType) && !_.isEmpty(plannerElementSets)) {
    updateCustomLabelsForUBDLearningGoals({
      field_list,
      curriculumType,
      plannerElementSets,
      t,
    });
  }

  if (!_.isEmpty(template)) {
    let _subjects = !_.isEmpty(subjects) ? subjects : courseSubjects;

    const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

    /**  In 2.0 orgs (learning courses enabled), when there is no unit plan (e.g. DP)
    and no class context, both subjects and courseSubjects can be empty.
    Fallback to getAssessmentSubjects to fetch subjects from the learning course cache
    so that subject-type checks (e.g. TOK) work correctly for removing fields like subjectLevel. */
    if (_.isEmpty(_subjects) && isLearningCoursesEnabled) {
      _subjects = getAssessmentSubjects({ courseId, learningCourseId }) || [];
    }

    const subjectType = _.get(_.first(_subjects), "type", SUBJECT_TYPE_GENERIC);

    /**
     * Remove the subjectLevel field for non-generic subject types (e.g. TOK, LD, PPS)
     * since those subjects don't have HL/SL levels.
     *
     * - GENERIC subjects always keep subjectLevel (they have HL/SL variants).
     * - In 2.0 orgs (learning courses enabled), OTHERS subjects also keep subjectLevel
     *   because they can have level variants in the new setup.
     * - In 1.0 orgs, OTHERS subjects behave like other special types and subjectLevel is removed.
     */
    const shouldRemoveSubjectLevel =
      subjectType !== SUBJECT_TYPE_GENERIC &&
      !(isLearningCoursesEnabled && subjectType === SUBJECT_TYPE_OTHERS);

    if (
      curriculumType === "IB_DP" &&
      (_.isEmpty(unitType) || unitType == UNIT_TYPE_DP) &&
      shouldRemoveSubjectLevel
    ) {
      updateStepList({
        step_list,
        fieldsToRemove: ["subjectLevel"],
      });
    }

    // Keep subjects for interdisciplinary or transdisciplinary unit plans
    if (
      !_.isEmpty(unitType) &&
      !_.includes(
        ["ibInquiry", "ibMYPInterdisciplinary", "ubdInquiry"],
        unitType
      )
    ) {
      //Remove MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC Option for non MYP Interdisciplinary Unit plan
      if (field_list["measureAssessing"]) {
        const index = _.findIndex(
          _.get(field_list["measureAssessing"], "config.options", []),
          {
            value: "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC",
          }
        );
        if (index >= 0) {
          field_list["measureAssessing"] = update(
            field_list["measureAssessing"],
            {
              config: { options: { $splice: [[index, 1]] } },
            }
          );
        }
      }

      // remove IDU objectives from learning goals for non-interdisciplinary unit plans
      if (curriculumType === "IB_MYP") {
        updateStepList({
          step_list,
          fieldsToRemove: ["interdisciplinaryCriteriaMYP"],
        });
      }
    }

    let isCustomWeights = false;
    if (isUsingNewGradingSetupInCourses) {
      isCustomWeights = isCustomWeightsV2;
    } else {
      isCustomWeights = getIsCustomWeights({
        curriculumProgramId: curriculumId,
        curriculumProgramType: curriculumType,
      });
    }

    if (!isCustomWeights) {
      updateStepList({
        step_list,
        fieldsToRemove: ["weightage"],
      });
    } else if (isFromInitializingModal) {
      updateFieldForTaskWeightageV3({ field_list, t });
    } else if (showTaskWeightageV3) {
      // Moving task weight to basic details instead of keeping it as a separate step
      _.update(step_list, "basicV2.fields", fields =>
        _.uniq([...(fields || []), "weightage"])
      );

      _.update(dynamicTemplate, "classroomEditSteps", steps =>
        _.without(steps || [], "maximumScore")
      );

      _.update(dynamicTemplate, "steps", steps =>
        _.without(steps || [], "maximumScore")
      );

      updateFieldForTaskWeightageV3({ field_list, t });
    }

    if (shouldRemoveCoverImage) {
      updateStepList({
        step_list,
        fieldsToRemove: ["image"],
      });
    }

    if (organizationId != resourceLibrarySourceOrg?.aiTutor) {
      updateStepList({
        step_list,
        fieldsToRemove: ["prompt", "templateLanguage"],
      });
    }

    if (!getIsEnabledLTITools() || _.isEmpty(ltiDropDownOptions)) {
      updateStepList({
        step_list,
        fieldsToRemove: ["ltiTemplate"],
      });
    }

    if (!isAIEvaluationExpanded || !isAIEvaluationEnabled) {
      updateStepList({
        step_list,
        fieldsToRemove: ["aiInstructions", "aiEvaluationLanguage"],
      });
    }

    if (getIsCourseBasedGradingEnabled()) {
      updateStepList({
        step_list,
        fieldsToRemove: ["subjects"],
      });
    }

    if (getIsSubjectBasedGradingEnabled()) {
      _.set(field_list, "subjects.config.isRequired", true);
    }

    if (getIsLearningCoursesEnabled()) {
      if (!getIsAssignmentsUnitTaggingIn2_0Enabled()) {
        updateStepList({
          step_list,
          fieldsToRemove: ["unitPlan"],
        });
      }
      const isNewGradingSetupEnabled = getIsConfigurationEnabledForCPAY({
        configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
      });
      if (!isNewGradingSetupEnabled) {
        updateStepList({
          step_list,
          fieldsToRemove: ["addLearningGoals", "enableRating"],
        });
      }
    }

    if (!getIsAITutorEnhancementsV2Enabled()) {
      updateStepList({
        step_list,
        fieldsToRemove: [
          "tutorSecondaryLanguage",
          "studentCommunicationMode",
          "tutorCommunicationMode",
        ],
      });
    }

    const isModalityFeatureDisabled =
      !ACLStore.can("FeatureFlag:DocumentModality") &&
      !ACLStore.can("FeatureFlag:WhiteboardModality") &&
      !ACLStore.can("FeatureFlag:CodeModality");

    if (!getIsAITutorModalitiesEnabled() || isModalityFeatureDisabled) {
      updateStepList({
        step_list,
        fieldsToRemove: ["openingMessageResource"],
      });
    }
  }

  const localisedTemplate = getLocalisedTemplate({
    template: { body: dynamicTemplate },
    t,
    templateType: "le",
  });

  updateDynamicLabelsForPlannerElements({
    field_list: dynamicTemplate.field_list,
    plannerElementSets,
  });

  // temporarily updating locale key based on getIsAITutorEnhancementsV2Enabled
  // to support both old and new labels
  if (!_.isEmpty(localisedTemplate) && getIsAITutorEnhancementsV2Enabled()) {
    const tutorLanguageConfig = _.get(
      localisedTemplate,
      "body.field_list.tutorLanguage.config",
      {}
    );

    if (!_.isEmpty(tutorLanguageConfig)) {
      const configUpdates = {
        label: t("ai:primary_tutor_language"),
        viewLabel: t("ai:primary_tutor_language"),
        subtext: t("unitPlanTemplate:le_PYP_AI_TUTOR_tutorLanguage_subtext"),
      };

      Object.assign(tutorLanguageConfig, configUpdates);
    }
  }

  return _.get(localisedTemplate, "body", {});
};

export const getDynamicTemplate = getMemoizedLangFunction(
  _getDynamicTemplate,
  ({
    unitType,
    curriculumType,
    plannerElementSets,
    subjects,
    courseSubjects,
    curriculumId,
    template,
    templateId,
    organizationId,
    ltiDropDownOptions,
    isFromInitializingModal,
    showTaskWeightageV3,
    isAIEvaluationExpanded,
    isAIEvaluationEnabled,
    isCustomWeightsV2,
    learningCourseId,
    courseId,
  }) => {
    return JSON.stringify({
      unitType,
      curriculumType,
      plannerElementSets: _.map(plannerElementSets, set =>
        _.pick(set, ["type", "label"])
      ),
      subjects: _.map(subjects, "id"),
      courseSubjects: _.map(courseSubjects, "id"),
      curriculumId,
      isTemplateEmpty: _.isEmpty(template),
      templateId,
      organizationId,
      ltiDropDownOptionSize: _.size(ltiDropDownOptions),
      isFromInitializingModal,
      showTaskWeightageV3,
      isAIEvaluationExpanded,
      isAIEvaluationEnabled,
      isCustomWeightsV2,
      learningCourseId,
      courseId,
    });
  }
);

export const getIsMYPInterdisciplinaryUnit = unitPlanFields => {
  const unitType = getFieldValue({
    fields: unitPlanFields,
    uid: "unitType",
  });

  return unitType === MYP_INTERDISCIPLINARY;
};

const AI_EXCLUDED_FIELDS = [
  "ageGroup",
  "grades",
  "criterias",
  "descriptorLevels",
  "checklistOptions",
  "objectiveLevel",
  "name",
  "summary",
  "chatHistory",
];

export const getExcludedFieldsForAI = params => {
  const {
    curriculumProgramType,
    unitPlanFields,
    assessmentDetails,
    unitPlanTemplate,
    excludedFields,
    template,
    prompts,
  } = params;

  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();
  const isNewGradingSetupEnabled = getIsNewGradingSetupEnabled();

  const unitType = getFieldValue({
    fields: unitPlanFields,
    uid: "unitType",
  });

  const localExcludedFields = [];

  if (isLearningCoursesEnabled && !isNewGradingSetupEnabled) {
    localExcludedFields.push("learningStandardUBD");
  }

  // 1. push the fields which are set in the excludedFields state
  localExcludedFields.push(
    ...(_.map(excludedFields, field => _.last(_.split(field, "_"))) || {})
  );

  // 2. we hide some learning goals for some subject groups like TOK, so we need to exclude them
  const subjects = _.get(
    _.find(assessmentDetails?.allFields, { uid: "subjects" }),
    "resolvedMinimalTree.subjects",
    []
  );
  localExcludedFields.push(
    ...getLearningGoalsToFilter({ unitType, subjects, source: "AI" })
  );

  // 3. if unit type is not interdisciplinary then exclude interdisciplinary criteria
  if (
    curriculumProgramType === CURRICULUM_TYPE_MYP &&
    !_.isEmpty(unitType) &&
    unitType !== MYP_INTERDISCIPLINARY
  ) {
    localExcludedFields.push("interdisciplinaryCriteriaMYP");
  }

  // 4. if context fields which are not present in the template then exclude them
  const allPromptFields = getModifiedContextFields(
    _.chain(prompts)
      .flatMap(prompt => _.get(prompt, "promptFields", []))
      .uniqBy(prompt => `${prompt.field}-${prompt.type}`)
      .filter(
        ({ field, type, config }) =>
          !config?.isLocalField &&
          !_.isEmpty(type) &&
          !_.includes(AI_EXCLUDED_FIELDS, field)
      )
      .value()
  );

  // assessment template step fields
  const assessmentSteps = _.uniq([
    ..._.get(template, "classroomEditSteps", []),
    ..._.get(template, "steps", []),
  ]);

  const assessmentStepFields = getStepFields({
    steps: assessmentSteps,
    template,
  });

  // unit template step fields
  const modules = _.get(unitPlanTemplate, "modules", []);
  const firstModule = _.get(
    unitPlanTemplate,
    `module_list.${_.first(modules)}`
  );
  const stages = _.get(firstModule, "stages", []);

  const unitPlanStepFields = _.reduce(
    stages,
    (result, stage) => {
      const stageObj = _.get(unitPlanTemplate, `stage_list.${stage}`);
      const stageSteps = _.get(stageObj, "steps", []);

      const stepFields = getStepFields({
        steps: stageSteps,
        template: unitPlanTemplate,
      });

      result.push(...stepFields);

      return result;
    },
    []
  );

  _.forEach(allPromptFields, ({ field, type }) => {
    const isUnit = type === UNIT;
    const isInterdisciplinaryUnit = getIsMYPInterdisciplinaryUnit(
      unitPlanFields
    );

    const interdisciplinaryField = INTERDISCIPLINARY_FIELD_MAPPING[field];
    const _field =
      isInterdisciplinaryUnit && interdisciplinaryField && isUnit
        ? _.lowerCase(interdisciplinaryField)
        : _.lowerCase(field);

    const fields = isUnit ? unitPlanStepFields : assessmentStepFields;

    const currentField = _.find(fields, key => {
      const _key = _.lowerCase(key);
      return _.isEqual(_key, _field) || _.startsWith(_key, _field);
    });

    if (_.isEmpty(currentField)) {
      localExcludedFields.push(`${type}.${field}`);
    }
  });

  // 5. exclude curriculum specific fields
  switch (curriculumProgramType) {
    case CURRICULUM_TYPE_DP: {
      const isSubjectTypeGeneric =
        _.get(_.first(subjects), "type", SUBJECT_TYPE_GENERIC) ===
        SUBJECT_TYPE_GENERIC;

      if (!isSubjectTypeGeneric) {
        return _.uniq([
          ...localExcludedFields,
          "subjectLevel",
          "unitSubjectLevel",
        ]);
      }

      return _.uniq(localExcludedFields);
    }
    default:
      return _.uniq(localExcludedFields);
  }
};

export const changeDisciplinaryContentToMultiField = getMemoizedFunction(
  params => {
    const { t, template, resolvedSubjectNodes, unitType } = params;

    if (_.isEmpty(template)) return {};

    if (unitType === MYP_INTERDISCIPLINARY) {
      const newTemplate = _.cloneDeep(template);

      const subFields = _.reduce(
        resolvedSubjectNodes,
        (acc, node) => {
          acc.push({
            config: {
              label: t("common:add_label1_for_label2_without_quotes", {
                label1: t("common:content_label"),
                label2: node?.name,
              }),
              subText: _.get(
                newTemplate,
                "body.field_list.disciplinaryContent.config.subtext"
              ),
            },
            key: node?.id,
            printType: "BaseComponent",
            type: "RichText",
            viewType: "TextView",
          });

          return acc;
        },
        []
      );

      return updateTemplate({
        ...params,
        template: update(newTemplate, {
          body: {
            field_list: {
              disciplinaryContent: {
                subFields: {
                  $set: subFields,
                },
                type: {
                  $set: "MultiField",
                },
              },
            },
          },
        }),
      });
    }

    return updateTemplate(params);
  }
);

export const getEntityTypeForAI = params => {
  const { groupType, entitySubType, curriculumType, assessmentType } = params;

  // for teacher preview of AI Tutor and student submission

  const isChatType = _.some(
    ["PREVIEW", "play_mode:", "SESSION_", "STUDENT_PREVIEW"],
    prefix => _.startsWith(entitySubType, prefix)
  );

  if (getIsAssessmentTypeConfigurationEnabled()) {
    if (isChatType) return "ai_tutor_chat";
    if (groupType === TASK_TYPE_AI_TUTOR) return "ai_tutor"; // passed "ai_tutor" instead of AI_TUTOR for backward compatibility

    return groupType;
  } else {
    if (isChatType) return `${ASSESSMENT_TYPE_AI_TUTOR}_chat`;

    switch (curriculumType) {
      case CURRICULUM_TYPE_UBD: {
        if (groupType === ASSESSMENT) return groupType;
        else return assessmentType;
      }
      // in PYP we don't have different prompts for fmt, smt so always use le prompts in any type of assessment
      case CURRICULUM_TYPE_PYP: {
        if (assessmentType === ASSESSMENT_TYPE_AI_TUTOR) return assessmentType;
        else return "le";
      }
      default: {
        return assessmentType;
      }
    }
  }
};

export const getUnitPlanDetails = ({ unitPlanDetails, assessmentTemplate }) => {
  return {
    ...unitPlanDetails,
    unitPlanFields: _.uniqBy(
      [
        ...combineUnitPlanMultiFieldData({
          unitPlanFields: _.cloneDeep(
            _.get(unitPlanDetails, "unitPlanFields", [])
          ),
          templateBody: assessmentTemplate,
        }),
        ...(unitPlanDetails?.unitPlanFields || {}),
      ],
      "uid"
    ),
  };
};

export const getMYPObjectiveLevelFromSubject = params => {
  const { objectiveLevelNodes, subjects, showSubject = false } = params;

  if (
    ACLStore.can("FeatureFlag:RemoveMYPObjectivesPhase") ||
    _.isEmpty(objectiveLevelNodes)
  ) {
    return "";
  }

  const objectiveLevels = _.reduce(
    subjects,
    (list, subject) => {
      const node = _.find(objectiveLevelNodes, objectiveNode => {
        return _.find(getNodeParents(objectiveNode, "SUBJECT"), {
          id: subject?.id,
        });
      });

      if (!_.isEmpty(node)) {
        if (showSubject) {
          list.push(_.join([node?.label, subject?.name], " - "));
        } else {
          list.push(node?.label);
        }
      }

      return list;
    },
    []
  );

  return objectiveLevels;
};

export const getRequiredFields = field_list => {
  return _.filter(_.keys(field_list), field => {
    const isRequired = _.get(field_list[field], "config.isRequired", false);
    return isRequired;
  });
};

export const getSubjectRecommendedPhaseMap = ({ subjects }) => {
  return _.reduce(
    subjects,
    (result, subject) => {
      const subjectId = subject?.id;
      const recommendedPhases = _.get(subject, "recommendedPhase", []);

      result[subjectId] = recommendedPhases;
      return result;
    },
    {}
  );
};

export const getLtiToolData = ({
  resourceFields,
  isLTIAlertApplicable = true,
}) => {
  if (!isLTIAlertApplicable) {
    return { isLtiToolAdded: false, ltiServiceName: "", ltiScoreMaximum: null };
  }

  // Helper function to find a resource field by UID and with non-empty attachment groups
  const findFieldWithAttachments = uid =>
    _.find(
      resourceFields,
      field =>
        _.get(field, "uid") === uid &&
        _.size(_.get(field, "resolvedMinimalTree.attachmentGroups", [])) > 0
    );

  // Find LTI Tool Object and Submission Template
  let templateField = findFieldWithAttachments("ltiTemplate");
  if (_.isNil(templateField)) {
    templateField = findFieldWithAttachments("studentTemplate");
  }
  //find lti attachment from general template field

  // Extract attachment groups from submissionTemplate
  const ltiAttachment = _.chain(templateField)
    .get("resolvedMinimalTree.attachmentGroups", [])
    .flatMap("attachments")
    .find(attachment => _.get(attachment, "metadata.type") === "LTI_TOOL")
    .value();

  // Get the LTI service name
  const ltiServiceName = _.get(ltiAttachment, "metadata.service", "");

  const ltiScoreMaximum = _.get(
    ltiAttachment,
    "metadata.lineitem.scoreMaximum",
    null
  );

  const isLtiToolAdded = !_.isNil(ltiAttachment);

  const result = { isLtiToolAdded, ltiServiceName, ltiScoreMaximum };

  return result;
};

export const getAttachmentGroups = resourceFields => {
  const studentTemplateField = _.find(resourceFields, {
    uid: "studentTemplate",
  });
  return _.get(
    studentTemplateField,
    "resolvedMinimalTree.attachmentGroups",
    []
  );
};

export const getTurnitinAttachments = resourceFields => {
  const attachmentGroups = getAttachmentGroups(resourceFields);
  const allAttachments = _.flatMap(attachmentGroups, group => {
    const attachments = _.get(group, "attachments", []);
    return _.isEmpty(attachments) ? [group] : attachments;
  });

  return _.filter(
    allAttachments,
    att =>
      _.get(att, "metadata.type") === "LTI_TOOL" &&
      _.get(att, "metadata.service") === "TURNITIN"
  );
};

export const updateAttachmentGroupsWithName = (attachmentGroups, newName) => {
  return _.map(attachmentGroups, group => ({
    ...group,
    attachments: _.map(_.get(group, "attachments", []), att =>
      _.get(att, "metadata.type") === "LTI_TOOL" &&
      _.get(att, "metadata.service") === "TURNITIN"
        ? { ...att, name: newName }
        : att
    ),
  }));
};

// BE no longer computes isLeaf on V2 wrappers, so derive it from children.
const withComputedIsLeaf = node => ({
  ...node,
  isLeaf: _.isEmpty(_.get(node, "children", [])),
});

export const withComputedIsLeafForNodes = nodes =>
  _.map(nodes, withComputedIsLeaf);

/**
 * This function extracts the objectives from nodes
 * It also handles the special case for objective node.
 * It also handles the special case if objectives are selected from multiple years
 */
export const getObjectivesFromNodes = ({ nodes: rawNodes }) => {
  // BE no longer computes isLeaf on these nodes; derive it from children
  // so downstream rubric tables receive a reliable flag.
  const nodes = withComputedIsLeafForNodes(rawNodes);

  const isMYPObjectiveRubric = !_.every(nodes, node => {
    return _.isEmpty(getAllNodeParents(node));
  });

  let objectives = orderNodes({
    nodes,
    startDepth: isMYPObjectiveRubric ? 1 : 2,
    gradeSequences: [],
  });

  const parents = _.filter(nodes, node => checkIsNodeTopParent({ node }));

  objectives = _.map(objectives, node => {
    const { isLeaf, parent, label } = node;

    if (isLeaf) {
      return node;
    } else {
      //Handle specially to convert objective to criteria
      const parentLabel = _.get(_.find(parents, { id: parent }), "label", "");

      const updatedLabel = _.replace(label, "MYP Objective", "MYP Criteria");
      const updatedParentLabel = _.replace(parentLabel, "MYP Objectives", "");
      //Handle specially if there are objectives selected from multiple years
      if (_.get(parents, "length", 0) > 1) {
        return { ...node, label: `${updatedParentLabel} - ${updatedLabel}` };
      } else {
        return { ...node, label: updatedLabel };
      }
    }
  });

  return objectives;
};

export const normalizePlannerElementNodeSets = connection => {
  const edges = _.get(connection, "edges", []);

  return _.map(edges, edge => {
    const setNode = _.get(edge, "node", {});
    const wrapper = _.get(setNode, "plannerElementNodeSetWrapperV2", null);
    const publishedEdges = _.get(wrapper, "publishedData.edges", []);
    const nodes = _.map(
      _.compact(_.map(publishedEdges, "node")),
      withComputedIsLeaf
    );
    const rootNodes = _.map(
      _.filter(nodes, n => _.get(n, "parent") == null),
      n => _.get(n, "id")
    );

    const plannerElementSet = _.get(setNode, "plannerElementSet");

    if (!plannerElementSet) return null;

    return {
      id: _.get(plannerElementSet, "id"),
      type: _.get(plannerElementSet, "type"),
      label: _.get(plannerElementSet, "label"),
      nodes,
      rootNodes,
    };
  });
};

export const toPlannerElementNodeFilters = filters => {
  if (_.isEmpty(filters)) return {};
  const { associatedParents, genericTags, ...rest } = filters;
  const result = { ...rest };
  const associatedParentsList = _.compact(_.castArray(associatedParents));
  _.forEach(associatedParentsList, ({ type, ids } = {}) => {
    if (_.isEmpty(ids)) return;
    if (type === "SUBJECT") result.subjects = ids;
    if (type === "GRADE") result.grades = ids;
  });
  if (!_.isEmpty(genericTags)) result.tags = genericTags;
  return result;
};
